import {
  newTransactionLimit,
  editTransactionLimit,
  deleteTransactionLimit,
  getTransactionLimit,
  editTransactionLimitByPeriod,
  newTransactionLimitByPeriod,
} from '@services/queries'
import {
  DELETE_LIMIT_MESSAGE,
  DELETE_LIMIT_MESSAGE_FAILURE,
  CREATE_LIMIT_MESSAGE,
  CREATE_LIMIT_MESSAGE_FAILURE,
  UPDATE_LIMIT_MESSAGE,
  UPDATE_LIMIT_MESSAGE_FAILURE,
} from '@constants'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import { verifyIncrease, verifySingleIncrease, capitalize } from '@utils'

export function useNewTransactionLimitMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (limit) => {
      return newTransactionLimit(limit.limitObject, limit.networkId)
    },
    {
      onSuccess({ data, response }, limit) {
        queryClient.invalidateQueries(['transaction_limit', limit.networkId])

        if (response?.data?.error?.description) {
          throw response?.data?.error?.description
        }

        if (onSuccess) {
          limit.handleCreateLimit(limit.limitObject, data?.result)
          onSuccess(data?.result)
        }
        if (data?.result) {
          store.addNotification(CREATE_LIMIT_MESSAGE(limit.limitObject.type))
        }
      },
      onError(err, limit) {
        console.error('Error updating player limits', err)
        store.addNotification(
          CREATE_LIMIT_MESSAGE_FAILURE(limit.limitObject.type),
        )
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useNewTransactionLimitByPeriodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (limits) => {
      if (Array.isArray(limits)) {
        return Promise.all(
          limits.map((limit) =>
            newTransactionLimitByPeriod(
              limit.limitObject,
              limit.networkId,
              limit.period,
            ),
          ),
        )
      }
      return newTransactionLimitByPeriod(
        limits.limitObject,
        limits.networkId,
        limits.period,
      )
    },
    {
      onSuccess(result, limits) {
        if (Array.isArray(limits)) {
          limits.forEach((limit, idx) => {
            const res = result[idx]
            const data = res?.data
            const response = res?.response
            queryClient.invalidateQueries([
              'transaction_limit',
              limit.networkId,
            ])
            if (response?.data?.error?.description) {
              throw response?.data?.error?.description
            }
            if (limit.handleCreateLimit) {
              limit.handleCreateLimit(limit.limitObject, data?.result)
            }
            if (onSuccess) {
              onSuccess(data?.result)
            }
            if (data?.result) {
              store.addNotification(
                CREATE_LIMIT_MESSAGE(limit.limitObject.type),
              )
            }
          })
        } else {
          const data = result?.data
          const response = result?.response
          queryClient.invalidateQueries(['transaction_limit', limits.networkId])
          if (response?.data?.error?.description) {
            throw response?.data?.error?.description
          }
          if (limits.handleCreateLimit) {
            limits.handleCreateLimit(limits.limitObject, data?.result)
          }
          if (onSuccess) {
            onSuccess(data?.result)
          }
          if (data?.result) {
            store.addNotification(CREATE_LIMIT_MESSAGE(limits.limitObject.type))
          }
        }
      },
      onError(err, limits) {
        console.error('Error updating player limits', err)
        const type = Array.isArray(limits)
          ? limits[0]?.limitObject?.type
          : limits.limitObject.type
        store.addNotification(CREATE_LIMIT_MESSAGE_FAILURE(type || 'limit'))
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useTransactionLimit(networkId) {
  const info = useQuery(
    ['transaction_limit', networkId],
    () => getTransactionLimit(networkId),
    {
      enabled: !!networkId,
    },
  )

  return info
}

export function useEditTransactionLimitMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (limit) => {
      return editTransactionLimit(limit.limitObject, limit.networkId)
    },
    {
      onSuccess({ data, response }, limit) {
        queryClient.invalidateQueries(['transaction_limit', limit.networkId])

        if (response?.data?.error?.description) {
          throw response?.data?.error?.description
        }

        if (onSuccess) onSuccess(data)

        const limitIncreased = verifyIncrease(data?.result, limit.limitObject)

        if (data?.result)
          store.addNotification(
            UPDATE_LIMIT_MESSAGE(limit.limitObject.type, limitIncreased),
          )
      },
      onError(err, limit) {
        console.error('Error updating player limits', err)
        store.addNotification(
          UPDATE_LIMIT_MESSAGE_FAILURE(limit.limitObject.type),
        )
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useEditTransactionLimitByPeriodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (limits) => {
      if (Array.isArray(limits)) {
        return Promise.all(
          limits.map((limit) =>
            editTransactionLimitByPeriod(
              limit.limitObject,
              limit.networkId,
              limit.period,
            ),
          ),
        )
      }
      return editTransactionLimitByPeriod(
        limits.limitObject,
        limits.networkId,
        limits.period,
      )
    },
    {
      onSuccess(result, limits) {
        if (Array.isArray(limits)) {
          limits.forEach((limit, idx) => {
            const res = result[idx]
            const data = res?.data
            const response = res?.response
            queryClient.invalidateQueries([
              'transaction_limit',
              limit.networkId,
            ])
            if (response?.data?.error?.description) {
              throw response?.data?.error?.description
            }
            if (onSuccess) onSuccess(data)
            const limitIncreased = verifySingleIncrease(
              data?.result?.limit,
              limit.limitObject.limit,
            )
            if (data?.result) {
              store.addNotification(
                UPDATE_LIMIT_MESSAGE(limit.limitObject.type, limitIncreased),
              )
            }
          })
        } else {
          const data = result?.data
          const response = result?.response
          queryClient.invalidateQueries(['transaction_limit', limits.networkId])
          if (response?.data?.error?.description) {
            throw response?.data?.error?.description
          }
          if (onSuccess) onSuccess(data)
          const limitIncreased = verifySingleIncrease(
            data?.result?.limit,
            limits.limitObject.limit,
          )
          if (data?.result) {
            store.addNotification(
              UPDATE_LIMIT_MESSAGE(limits.limitObject.type, limitIncreased),
            )
          }
        }
      },
      onError(err, limits) {
        console.error('Error updating player limits', err)
        const type = Array.isArray(limits)
          ? limits[0]?.limitObject?.type
          : limits.limitObject.type
        store.addNotification(UPDATE_LIMIT_MESSAGE_FAILURE(type || 'limit'))
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useDeleteTransactionLimitMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (limit) => {
      // `segment` is the URL path segment; legacy callers omit it and pass the
      // path value as `type` ('deposit'), so fall back to `limit.type`.
      // `queryType` is the OPTIONAL ?type= query param the RTS delete uses to
      // target gross_deposit / true_loss (PHOENIX-692); legacy callers leave it
      // undefined so no `type` query param is appended.
      return deleteTransactionLimit(
        limit.networkId,
        limit.segment ?? limit.type,
        limit.currency,
        limit.queryType,
      )
    },
    {
      onSuccess(data, limit) {
        queryClient.invalidateQueries(['transaction_limit', limit.networkId])
        if (onSuccess) onSuccess(data)
        // Optional: the RTS delete relies on query invalidation to refresh the
        // view (pending_action: 'removal') and does not pass this callback.
        if (limit.handleDeleteLimitResponse) {
          limit.handleDeleteLimitResponse(limit.update)
        }
        store.addNotification(DELETE_LIMIT_MESSAGE(limit.type))
      },
      onError(err, limit) {
        console.error('Error updating player limits', err)
        store.addNotification(DELETE_LIMIT_MESSAGE_FAILURE(limit.type))
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}
